import React, { createContext, useState, useContext, useEffect } from 'react';
import * as configuracionApiService from '@/services/configuracionApiService';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { useSucursal } from '@/context/SucursalContext';
import { normalizeDataObject } from '../utils/normalizationUtils';

// Crear el contexto
const ConfiguracionContext = createContext();

// Hook personalizado para usar el contexto
export const useConfiguracion = () => useContext(ConfiguracionContext);

// Proveedor del contexto
export const ConfiguracionProvider = ({ children }) => {
  const { empresaActual } = useSimpleEmpresa();
  const sucursalContext = useSucursal?.() || {};
  const selectedSucursal = sucursalContext.selectedSucursal;
  const [configuracion, setConfiguracion] = useState({
    NombreTaller: 'Revvly',
    DireccionTaller: '',
    TelefonoTaller: '',
    EmailTaller: '',
    Logo: null
  });
  const [loading, setLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState({
    enablePatentLookup: true,
    enableVehicleCatalog: true
  });

  // Cargar la configuración al montar el componente
  useEffect(() => {
    const fetchConfiguracion = async () => {
      try {
        // Solo cargar configuración si hay una empresa Y sucursal seleccionadas
        if (!empresaActual?.id || !selectedSucursal) {
          setLoading(false);
          return;
        }

        // Obtener configuraciones individuales usando el nuevo servicio de configuración
        const [nombreTaller, direccionTaller, telefonoTaller, emailTaller, logoBase64, enablePatentLookup, enableVehicleCatalog] = await Promise.all([
          configuracionApiService.getConfiguracionPorClave('nombre_empresa').catch(() => 'Revvly'),
          configuracionApiService.getConfiguracionPorClave('direccion_empresa').catch(() => ''),
          configuracionApiService.getConfiguracionPorClave('telefono_empresa').catch(() => ''),
          configuracionApiService.getConfiguracionPorClave('email_empresa').catch(() => ''),
          configuracionApiService.getConfiguracionPorClave('logo_empresa_base64').catch(() => null),
          configuracionApiService.getConfiguracionPorClave('enable_patent_lookup').catch(() => process.env.REACT_APP_ENABLE_PATENT_LOOKUP || 'false'),
          configuracionApiService.getConfiguracionPorClave('enable_vehicle_catalog').catch(() => process.env.REACT_APP_ENABLE_VEHICLE_CATALOG || 'true')
        ]);

        /* //console.log('🔍 ConfiguracionContext - Datos obtenidos:', {
          nombreTaller,
          direccionTaller,
          telefonoTaller,
          emailTaller,
          logoBase64,
          enablePatentLookup,
          enableVehicleCatalog
        }); */

        const config = {
          NombreTaller: nombreTaller || 'Revvly',
          DireccionTaller: direccionTaller || '',
          TelefonoTaller: telefonoTaller || '',
          EmailTaller: emailTaller || '',
          Logo: logoBase64 || null
        };

        // Actualizar feature flags
        setFeatureFlags({
          enablePatentLookup: enablePatentLookup === 'true' || enablePatentLookup === true,
          enableVehicleCatalog: enableVehicleCatalog === 'true' || enableVehicleCatalog === true
        });

        /* //console.log('🔍 ConfiguracionContext - Config final:', config); */
        // Normalizar configuración (manteniendo compatibilidad)
        const normalizedConfig = normalizeDataObject(config, 'configuracion');
        setConfiguracion(normalizedConfig);
        
        // Actualizar el título de la página con el nombre del taller
        if (config.NombreTaller) {
          document.title = config.NombreTaller;
        }
      } catch (error) {
        // Usar valores por defecto en caso de error
        const defaultConfig = {
          NombreTaller: 'Revvly',
          DireccionTaller: '',
          TelefonoTaller: '',
          EmailTaller: '',
          Logo: null
        };
        const normalizedDefaultConfig = normalizeDataObject(defaultConfig, 'configuracion');
        setConfiguracion(normalizedDefaultConfig);
      } finally {
        setLoading(false);
      }
    };

    fetchConfiguracion();
  }, [empresaActual?.id, selectedSucursal]); // Re-cargar cuando cambie la empresa o sucursal

  // Función para recargar la configuración desde la base de datos
  const reloadConfiguracion = async () => {
    try {
      // Solo cargar configuración si hay una empresa seleccionada
      if (!empresaActual?.id) {
        return;
      }

      setLoading(true);

      // Obtener configuraciones individuales usando el nuevo servicio de configuración
      const [nombreTaller, direccionTaller, telefonoTaller, emailTaller, logoBase64, enablePatentLookup, enableVehicleCatalog] = await Promise.all([
        configuracionApiService.getConfiguracionPorClave('nombre_empresa').catch(() => 'Revvly'),
        configuracionApiService.getConfiguracionPorClave('direccion_empresa').catch(() => ''),
        configuracionApiService.getConfiguracionPorClave('telefono_empresa').catch(() => ''),
        configuracionApiService.getConfiguracionPorClave('email_empresa').catch(() => ''),
        configuracionApiService.getConfiguracionPorClave('logo_empresa_base64').catch(() => null),
        configuracionApiService.getConfiguracionPorClave('enable_patent_lookup').catch(() => process.env.REACT_APP_ENABLE_PATENT_LOOKUP || 'false'),
        configuracionApiService.getConfiguracionPorClave('enable_vehicle_catalog').catch(() => process.env.REACT_APP_ENABLE_VEHICLE_CATALOG || 'false')
      ]);

      const config = {
        NombreTaller: nombreTaller || 'Revvly',
        DireccionTaller: direccionTaller || '',
        TelefonoTaller: telefonoTaller || '',
        EmailTaller: emailTaller || '',
        Logo: logoBase64 || null
      };

      // Actualizar feature flags en reload
      setFeatureFlags({
        enablePatentLookup: enablePatentLookup === 'true' || enablePatentLookup === true,
        enableVehicleCatalog: enableVehicleCatalog === 'true' || enableVehicleCatalog === true
      });

      // Normalizar configuración reloaded
      const normalizedConfig = normalizeDataObject(config, 'configuracion');
      setConfiguracion(normalizedConfig);
      
      // Actualizar el título de la página con el nombre del taller
      if (config.NombreTaller) {
        document.title = config.NombreTaller;
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar la configuración
  const updateConfiguracion = async (newConfig) => {
    try {
      // Verificar que hay una empresa seleccionada
      if (!empresaActual?.id) {
        throw new Error('No hay empresa seleccionada');
      }

      // Actualizar cada configuración individualmente con filtro de empresa
      const updates = [];
      
      if (newConfig.NombreTaller !== undefined) {
        updates.push(configuracionApiService.setConfiguracion({
          clave: 'nombre_empresa',
          valor: newConfig.NombreTaller,
          tipo: 'string',
          descripcion: 'Nombre de la empresa',
          empresa_id: empresaActual.id,
          editable_por_taller: true
        }));
      }
      if (newConfig.DireccionTaller !== undefined) {
        updates.push(configuracionApiService.setConfiguracion({
          clave: 'direccion_empresa',
          valor: newConfig.DireccionTaller,
          tipo: 'string',
          descripcion: 'Dirección de la empresa',
          empresa_id: empresaActual.id,
          editable_por_taller: true
        }));
      }
      if (newConfig.TelefonoTaller !== undefined) {
        updates.push(configuracionApiService.setConfiguracion({
          clave: 'telefono_empresa',
          valor: newConfig.TelefonoTaller,
          tipo: 'string',
          descripcion: 'Teléfono de la empresa',
          empresa_id: empresaActual.id,
          editable_por_taller: true
        }));
      }
      if (newConfig.EmailTaller !== undefined) {
        updates.push(configuracionApiService.setConfiguracion({
          clave: 'email_empresa',
          valor: newConfig.EmailTaller,
          tipo: 'string',
          descripcion: 'Email de contacto',
          empresa_id: empresaActual.id,
          editable_por_taller: true
        }));
      }
      if (newConfig.Logo !== undefined) {
        updates.push(configuracionApiService.setConfiguracion({
          clave: 'logo_empresa_base64',
          valor: newConfig.Logo,
          tipo: 'string',
          descripcion: 'Logo de la empresa en base64',
          empresa_id: empresaActual.id,
          editable_por_taller: true
        }));
      }

      // Esperar a que todas las actualizaciones se completen
      await Promise.all(updates);

      // Actualizar el estado local
      const updatedConfig = { ...configuracion, ...newConfig };
      // Normalizar configuración actualizada
      const normalizedUpdatedConfig = normalizeDataObject(updatedConfig, 'configuracion');
      setConfiguracion(normalizedUpdatedConfig);
      
      // Actualizar el título de la página si cambia el nombre del taller
      if (normalizedUpdatedConfig.NombreTaller) {
        document.title = normalizedUpdatedConfig.NombreTaller;
      }
      
      return normalizedUpdatedConfig;
    } catch (error) {
      throw error;
    }
  };

  return (
    <ConfiguracionContext.Provider 
      value={{ 
        configuracion, 
        loading, 
        updateConfiguracion,
        reloadConfiguracion,
        featureFlags
      }}
    >
      {children}
    </ConfiguracionContext.Provider>
  );
};

export default ConfiguracionContext;
